import { useCallback, useEffect, useMemo, useState } from 'react';

import { createColumnHelper, getCoreRowModel, getPaginationRowModel, useReactTable } from '@tanstack/react-table';
import { useParams, useSearchParams } from 'react-router-dom';

import { DeletionStatus } from '../../../../../../../../../backend/src/types/types';
import { FileHistoryLog, LogEvent, UploadLogEvent } from '../components/file-history-log';
import { DeletionDetailModal } from '../components/modals/deletion-detail-modal';
import { UploadDetailModal } from '../components/modals/upload-detail-modal';
import { formatNumber } from '../utils';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useGetDeletedFiles } from '@/api/queries/useGetDeletedFiles';
import { useGetMatterUploadHistory } from '@/api/queries/useGetMatterUploadHistory';
import { DeletionHistory, UploadHistory } from '@/api/types/backend-dto-types';
import formatDate from '@/helpers/formatDate';
import { Icon } from '@/new/components/icon';
import { ProcessingChip, ProcessingState } from '@/new/components/processing-chip';

function buildLogEvents(uploads: UploadHistory[] | undefined, deletions: DeletionHistory[] | undefined): LogEvent[] {
  const events: LogEvent[] = [];

  // Add upload events
  uploads?.forEach((upload) => {
    const attempted = (upload.successfullyUploadedFileCount ?? 0) + (upload.uploadFailures?.length ?? 0);
    events.push({
      id: upload.id,
      type: 'upload',
      date: upload.createdAt as unknown as string, // Date type in entity, string from JSON
      userEmail: upload.createdBy?.email ?? 'Unknown',
      status: upload.status,
      attempted,
      successful: upload.successfullyUploadedFileCount ?? 0,
      skipped: upload.uploadFailures?.length ?? 0,
    });
  });

  // Add deletion events
  deletions?.forEach((deletion) => {
    events.push({
      id: deletion.id,
      type: 'deletion',
      date: deletion.createdAt,
      userEmail: deletion.createdBy?.email ?? 'Unknown',
      count: deletion.fileDeletion?.length ?? 0,
      deletionType: deletion.type,
      status: deletion.status,
      totalDocs: deletion.totalDocs,
      completedDocs: deletion.completedDocs,
    });
  });

  // Sort by date descending (newest first)
  events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return events;
}

const columnHelper = createColumnHelper<LogEvent>();

function getUploadStatusChip(status: UploadHistory['status']) {
  if (status === 'failed') {
    return <ProcessingChip processingState={ProcessingState.Failed} showIcon={false} />;
  }
  if (status === 'cancelled') {
    return <ProcessingChip processingState={ProcessingState.Cancelled} showIcon={false} />;
  }
  if (status === 'processing') {
    return <ProcessingChip processingState={ProcessingState.Processing} showIcon={false} />;
  }
  return <ProcessingChip processingState={ProcessingState.Complete} showIcon={false} />;
}

function getDeletionStatusChip(status: DeletionStatus | null) {
  if (status === DeletionStatus.Failed) {
    return <ProcessingChip processingState={ProcessingState.Failed} showIcon={false} />;
  }
  if (status === DeletionStatus.Processing) {
    return (
      <ProcessingChip processingState={ProcessingState.Processing} processingText="In progress" showIcon={false} />
    );
  }
  return <ProcessingChip processingState={ProcessingState.Complete} showIcon={false} />;
}

const columns = [
  columnHelper.accessor('date', {
    header: () => <span>Date</span>,
    cell: (info) => <span className="text-xs text-gray-500">{formatDate(info.getValue(), true, true)}</span>,
  }),
  columnHelper.accessor('type', {
    header: () => <span>Type</span>,
    cell: (info) => {
      const type = info.getValue();
      return <span className="text-xs text-gray-800">{type === 'upload' ? 'Files Uploaded' : 'Files Deleted'}</span>;
    },
  }),
  columnHelper.accessor('userEmail', {
    header: () => <span>User</span>,
    cell: (info) => <span className="text-xs text-gray-800">{info.getValue()}</span>,
  }),
  columnHelper.display({
    id: 'status',
    header: () => <span>Status</span>,
    cell: (info) => {
      const event = info.row.original;
      if (event.type === 'upload') {
        return getUploadStatusChip(event.status);
      }
      return getDeletionStatusChip(event.status);
    },
  }),
  columnHelper.display({
    id: 'summary',
    header: () => <span>Summary</span>,
    cell: (info) => {
      const event = info.row.original;
      if (event.type === 'upload') {
        const upload = event as UploadLogEvent;
        return (
          <div className="flex items-center gap-2 text-xs">
            <div className="flex items-center gap-1.5 rounded-md bg-blue-50 px-2 py-1">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
              <span className="text-blue-700">{formatNumber(upload.attempted)} attempted</span>
            </div>
            <Icon className="lucide--arrow-right h-3 w-3 text-gray-300" />
            <div className="flex items-center overflow-hidden rounded-md">
              <div className="flex items-center gap-1.5 bg-green-50 px-2 py-1">
                <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                <span className="text-green-700">{formatNumber(upload.successful)} uploaded</span>
              </div>
              <div className="flex items-center gap-1.5 bg-amber-50 px-2 py-1">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                <span className="text-amber-700">{formatNumber(upload.skipped)} skipped</span>
              </div>
            </div>
          </div>
        );
      }
      // Deletion event
      const deletionLabel =
        event.status === DeletionStatus.Failed && event.totalDocs !== null
          ? `${formatNumber(event.completedDocs ?? 0)} of ${formatNumber(event.totalDocs)} deleted`
          : event.status === DeletionStatus.Processing
            ? `Deleting ${formatNumber(event.totalDocs ?? event.count)}`
            : `${formatNumber(event.count)} deleted`;
      return (
        <div className="flex items-center overflow-hidden text-xs">
          <div className="flex items-center gap-1.5 rounded-md bg-red-50 px-2 py-1">
            <span className="h-1.5 w-1.5 rounded-full bg-red-600" />
            <span className="text-red-700">{deletionLabel}</span>
          </div>
        </div>
      );
    },
  }),
];

export function FileLog() {
  const { matterId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  if (!matterId) return null;

  const { data: uploads, isLoading } = useGetMatterUploadHistory(matterId);
  const { data: deletions, isLoading: isLoadingDeletions } = useGetDeletedFiles(matterId);

  const [selectedUploadId, setSelectedUploadId] = useState<string | null>(null);
  const [selectedDeletionId, setSelectedDeletionId] = useState<string | null>(null);

  // Auto-open upload detail modal from ?upload=<id> query param
  useEffect(() => {
    const uploadParam = searchParams.get('upload');
    if (uploadParam) {
      setSelectedUploadId(uploadParam);
      searchParams.delete('upload');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  const logEvents = useMemo(() => buildLogEvents(uploads, deletions), [uploads, deletions]);
  const isLogLoading = isLoading || isLoadingDeletions;

  const logTable = useReactTable({
    data: logEvents,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableSorting: false,
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  const handleEventClick = useCallback(
    (event: LogEvent) => {
      if (event.type === 'upload') {
        trackCustomEvent(TrackingEventsV2.FileHistoryClickUploadEvent, { matterId, eventId: event.id });
        setSelectedUploadId(event.id);
      } else {
        trackCustomEvent(TrackingEventsV2.FileHistoryClickDeletionEvent, { matterId, eventId: event.id });
        setSelectedDeletionId(event.id);
      }
    },
    [matterId],
  );

  return (
    <>
      <FileHistoryLog
        isLogLoading={isLogLoading}
        logEvents={logEvents}
        logTable={logTable}
        onEventClick={handleEventClick}
      />
      {selectedUploadId && (
        <UploadDetailModal
          open={!!selectedUploadId}
          onOpenChange={(open) => !open && setSelectedUploadId(null)}
          matterId={matterId}
          uploadId={selectedUploadId}
        />
      )}
      {selectedDeletionId && (
        <DeletionDetailModal
          open={!!selectedDeletionId}
          onOpenChange={(open) => !open && setSelectedDeletionId(null)}
          matterId={matterId}
          deletionId={selectedDeletionId}
        />
      )}
    </>
  );
}
